import { useState } from 'react';

import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/shadcn/button';
import { Calendar } from '@/components/shadcn/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/popover';

type Props = {
  id?: string;
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  required?: boolean;
  startMonth?: Date;
  endMonth?: Date;
};

const YEARS_AROUND_TODAY = 10;

export function DatePicker({
  id,
  value,
  onChange,
  required,
  startMonth,
  endMonth,
}: Props) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const currentYear = new Date().getFullYear();
  const defaultStartMonth = new Date(currentYear - YEARS_AROUND_TODAY, 0);
  const defaultEndMonth = new Date(currentYear + YEARS_AROUND_TODAY, 11);

  const handleChange = (date: Date | undefined) => {
    setIsOpen(false);
    onChange(date);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          data-empty={!value}
          className="data-[empty=true]:text-muted-foreground w-70 justify-start text-left font-normal"
        >
          <CalendarIcon />
          {value ? format(value, 'dd/MM/yyyy') : t('DATE_PICKER_PLACEHOLDER')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={value}
          onSelect={handleChange}
          required={required}
          defaultMonth={value}
          captionLayout="dropdown"
          startMonth={startMonth ?? defaultStartMonth}
          endMonth={endMonth ?? defaultEndMonth}
        />
      </PopoverContent>
    </Popover>
  );
}
